// Admin editor for the regional default syllabi. A new feis starts with its
// region's default competitions (backend controller/feis.js create), so this
// screen is where that starting point is kept. Changes affect feiseanna created
// afterwards; existing events keep their own syllabus. Built on the organizer
// Oireachtas panel's .oir design layer (Devin 2026-09-28).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderOrg from '../Headers/HeaderOrg';
import { regionData } from '../../helper/DataArrays';
import { Msr, useMaterialSymbols } from '../OireachtasPanel/symbols';
import { createRow, deleteRow, getRegionRows, getRegions, updateRow } from './api';
import {
  AGE_OPTIONS,
  ELIGIBLE_LEVELS,
  KNOWN_GROUPS,
  agesLabel,
  dancersLabel,
  emptyForm,
  formToPayload,
  formatRules,
  groupRows,
  matchesSearch,
  rowToForm,
  suggestChampionship,
} from './rows';
import '../OireachtasPanel/oireachtas.css';
import '../OireachtasPanel/organizerOireachtas.css';
import './defaultSyllabi.css';

const errText = (e) => e?.messages || e?.message || 'Something went wrong. Please try again.';
const money = (v) => (Number(v) ? `$${Number(v).toFixed(2)}` : 'Free');
const titleCase = (s) =>
  String(s || '').toLowerCase().replace(/(^|[\s-])(\w)/g, (m, sep, c) => sep + c.toUpperCase());

const ToggleChip = ({ on, onClick, children, label }) => (
  <button type="button" className={`ds-tog${on ? ' on' : ''}`} aria-pressed={on} aria-label={label} onClick={onClick}>
    {on && <Msr>check</Msr>}
    {children}
  </button>
);

const Segmented = ({ value, options, onChange, label }) => (
  <div className="ds-seg" role="radiogroup" aria-label={label}>
    {options.map((o) => (
      <button
        key={o}
        type="button"
        role="radio"
        aria-checked={value === o}
        className={value === o ? 'on' : ''}
        onClick={() => onChange(o)}
      >
        {o}
      </button>
    ))}
  </div>
);

// Modal focus: move focus into the dialog, keep Tab inside it, close on
// Escape (unless busy), make the page behind inert, and put focus back on
// close.
function useDialogFocus(ref, onClose, busy) {
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  onCloseRef.current = onClose;
  busyRef.current = busy;
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const previous = document.activeElement;
    const behind = document.querySelector('.ds-page .wrap');
    behind?.setAttribute('inert', '');
    behind?.setAttribute('aria-hidden', 'true');
    const focusables = () =>
      [...root.querySelectorAll('button, input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
        (el) => !el.disabled && el.offsetParent !== null
      );
    (root.querySelector('[data-autofocus]') || focusables()[0])?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !busyRef.current) {
        e.preventDefault();
        onCloseRef.current();
      } else if (e.key === 'Tab') {
        const list = focusables();
        if (!list.length) {
          // every control disabled (e.g. while deleting): keep focus on the dialog
          e.preventDefault();
          root.focus();
          return;
        }
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    root.addEventListener('keydown', onKey);
    return () => {
      root.removeEventListener('keydown', onKey);
      behind?.removeAttribute('inert');
      behind?.removeAttribute('aria-hidden');
      if (previous && document.contains(previous)) previous.focus();
    };
  }, [ref]);
}

const Field = ({ label, error, hint, children, htmlFor }) => (
  <div className={`ds-field${error ? ' bad' : ''}`}>
    <label className="ds-label" htmlFor={htmlFor}>{label}</label>
    {children}
    {error ? <div className="pc-err">{error}</div> : hint ? <div className="ds-hint">{hint}</div> : null}
  </div>
);

function RowEditor({ editor, groups, region, onChange, onCancel, onSave }) {
  const { form, errors = {}, saving, serverError, id } = editor;
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef, onCancel, saving);
  // editing a field clears its error (girls/boys share the "dancers" error)
  const set = (patch) => {
    const cleared = { ...errors };
    Object.keys(patch).forEach((k) => delete cleared[k === 'girls' || k === 'boys' ? 'dancers' : k]);
    const next = { ...form, ...patch };
    // until the admin sets it, the Championship box follows level, name and eligibility
    if (!next.championshipTouched && ('group' in patch || 'description' in patch || 'levels' in patch)) {
      next.championship = suggestChampionship(next);
    }
    onChange({ ...editor, form: next, errors: cleared });
  };
  const toggle = (key, value) =>
    set({ [key]: form[key].includes(value) ? form[key].filter((v) => v !== value) : [...form[key], value] });

  return (
    <div className="modal-scrim" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !saving && onCancel()}>
      <div className="omodal ds-modal" role="dialog" aria-modal="true" aria-labelledby="ds-edit-title" ref={dialogRef} tabIndex={-1}>
        <div className="ds-modal-h">
          <div className="op-title" id="ds-edit-title" role="heading" aria-level="2">
            {id ? 'Edit competition' : 'Add competition'}
          </div>
          <div className="op-note">{region} default syllabus</div>
        </div>
        <div className="ds-modal-b">
          {serverError && <div className="op-error">{serverError}</div>}
          <Field label="Level" htmlFor="ds-group" error={errors.group} hint="Pick one or type a new level name.">
            <input
              id="ds-group"
              data-autofocus
              className="ds-input"
              list="ds-group-options"
              value={form.group}
              onChange={(e) => set({ group: e.target.value.toUpperCase() })}
              autoComplete="off"
            />
            <datalist id="ds-group-options">
              {groups.map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </Field>
          <div className="ds-row2">
            <Field label="Code" htmlFor="ds-code" error={errors.code}>
              <input id="ds-code" className="ds-input" value={form.code} onChange={(e) => set({ code: e.target.value })} autoComplete="off" />
            </Field>
            <Field label="Description" htmlFor="ds-desc" error={errors.description}>
              <input id="ds-desc" className="ds-input" value={form.description} onChange={(e) => set({ description: e.target.value })} autoComplete="off" />
            </Field>
          </div>
          <Field label="Ages" error={errors.ages}>
            <div className="ds-chips">
              {AGE_OPTIONS.map((a) => (
                <ToggleChip key={a} on={form.ages.includes(a)} onClick={() => toggle('ages', a)}>
                  {a === '22 AND OVER' ? '22 & over' : a}
                </ToggleChip>
              ))}
            </div>
          </Field>
          <Field label="Open to levels" hint="Which dancer levels may enter (used by registration).">
            <div className="ds-chips">
              {ELIGIBLE_LEVELS.map((l) => (
                <ToggleChip key={l} on={form.levels.includes(l)} onClick={() => toggle('levels', l)}>
                  {titleCase(l)}
                </ToggleChip>
              ))}
            </div>
          </Field>
          <Field label="Dancers" error={errors.dancers}>
            <div className="ds-chips">
              <ToggleChip on={form.girls} onClick={() => set({ girls: !form.girls })}>Girls</ToggleChip>
              <ToggleChip on={form.boys} onClick={() => set({ boys: !form.boys })}>Boys</ToggleChip>
            </div>
          </Field>
          <div className="ds-row2">
            <Field label="Rounds">
              <Segmented label="Rounds" value={form.rounds} options={[1, 2, 3]} onChange={(v) => set({ rounds: v })} />
            </Field>
            <Field label="Judges">
              <Segmented label="Judges" value={form.adjudicators} options={[1, 3, 5]} onChange={(v) => set({ adjudicators: v })} />
            </Field>
          </div>
          <div className="ds-row2">
            <Field label="Price" htmlFor="ds-price" error={errors.price}>
              <div className="pc-input">
                <span>$</span>
                <input id="ds-price" inputMode="decimal" value={form.price} onChange={(e) => set({ price: e.target.value })} />
              </div>
            </Field>
            <Field label="Maximum competitors" htmlFor="ds-max" error={errors.max_participants} hint="0 means no limit.">
              <div className="pc-input">
                <input id="ds-max" inputMode="numeric" value={form.max_participants} onChange={(e) => set({ max_participants: e.target.value })} />
              </div>
            </Field>
          </div>
          <div className="ds-flag">
            <button
              type="button"
              className={`toggle-f${form.championship ? ' on' : ''}`}
              aria-pressed={form.championship}
              onClick={() => set({ championship: !form.championship, championshipTouched: true })}
            >
              <span className="sw" />
              Championship competition
            </button>
            <div className="ds-hint">
              A dancer entered in any championship competition counts as championship for event caps; everyone else counts as grades.
            </div>
          </div>
          <button
            type="button"
            className={`toggle-f${form.second_chance ? ' on' : ''}`}
            aria-pressed={form.second_chance}
            onClick={() => set({ second_chance: !form.second_chance })}
          >
            <span className="sw" />
            Second chance competition
          </button>
        </div>
        <div className="modal-act ds-modal-act">
          <button type="button" className="mbtn ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="ab-next" onClick={onSave} disabled={saving}>
            <Msr>check</Msr>
            {saving ? 'Saving…' : id ? 'Save changes' : 'Add competition'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteDialog({ row, region, onKeep, onRemove }) {
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef, onKeep, row.busy);
  useEffect(() => {
    if (row.busy) dialogRef.current?.focus();
  }, [row.busy]);
  return (
    <div className="modal-scrim" role="presentation">
      <div className="omodal" role="alertdialog" aria-modal="true" aria-labelledby="ds-del-title" ref={dialogRef} tabIndex={-1}>
        <div className="modal-ic"><Msr>delete</Msr></div>
        <h3 id="ds-del-title">Remove {row.code}?</h3>
        <p>
          {row.description} ({agesLabel(row.ages)}) will no longer be added to new {region} feiseanna.
          Existing events are not changed.
        </p>
        {row.error && <div className="op-error">{row.error}</div>}
        <div className="modal-act">
          <button type="button" className="mbtn ghost" onClick={onKeep} disabled={row.busy} data-autofocus>Keep it</button>
          <button type="button" className="mbtn danger" onClick={onRemove} disabled={row.busy}>
            {row.busy ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DefaultSyllabi() {
  useMaterialSymbols();
  const navigate = useNavigate();
  const [regionStats, setRegionStats] = useState([]);
  const [region, setRegion] = useState(regionData[0].value);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [editor, setEditor] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [notice, setNotice] = useState('');

  const loadRegions = useCallback(async () => {
    try {
      const res = await getRegions();
      setRegionStats(res.regions || []);
    } catch (e) {
      setError(errText(e));
    }
  }, []);

  // only the latest request may update the screen (switching regions while
  // one is loading must not show the previous region's rows)
  const requestSeq = useRef(0);
  const loadRows = useCallback(async (r) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    try {
      const res = await getRegionRows(r);
      if (seq !== requestSeq.current) return;
      setRows(res.competitions || []);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setError(errText(e));
      setRows([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);
  useEffect(() => {
    setGroupFilter('');
    loadRows(region);
  }, [region, loadRows]);

  const regions = useMemo(() => {
    const names = new Set(regionData.map((r) => r.value));
    regionStats.forEach((r) => names.add(r.region));
    return [...names].map((name) => ({
      name,
      count: regionStats.find((r) => r.region === name)?.competitions || 0,
    }));
  }, [regionStats]);

  const grouped = useMemo(() => groupRows(rows), [rows]);
  const groupNames = grouped.map((g) => g.group);
  const visible = useMemo(
    () =>
      grouped
        .filter((g) => !groupFilter || g.group === groupFilter)
        .map((g) => ({ ...g, rows: g.rows.filter((r) => matchesSearch(r, q)) }))
        .filter((g) => g.rows.length > 0),
    [grouped, groupFilter, q]
  );
  const editorGroups = useMemo(() => [...new Set([...groupNames, ...KNOWN_GROUPS])], [groupNames]);

  const openAdd = () => setEditor({ id: null, form: emptyForm(groupFilter), errors: {} });
  const openEdit = (row) => setEditor({ id: row._id, form: rowToForm(row), errors: {} });

  const save = async () => {
    const { payload, errors } = formToPayload(editor.form);
    if (errors) return setEditor({ ...editor, errors, serverError: '' });
    setEditor({ ...editor, errors: {}, saving: true, serverError: '' });
    try {
      if (editor.id) await updateRow(editor.id, payload);
      else await createRow(region, payload);
      setNotice(editor.id ? `Saved ${payload.code}.` : `Added ${payload.code} to ${region}.`);
      setEditor(null);
      await Promise.all([loadRows(region), loadRegions()]);
    } catch (e) {
      setEditor((cur) => cur && { ...cur, saving: false, serverError: errText(e) });
    }
  };

  const confirmDelete = async () => {
    const row = pendingDelete;
    setPendingDelete({ ...row, busy: true });
    try {
      await deleteRow(row._id);
      setNotice(`Removed ${row.code} from ${region}.`);
      setPendingDelete(null);
      await Promise.all([loadRows(region), loadRegions()]);
    } catch (e) {
      setPendingDelete({ ...row, busy: false, error: errText(e) });
    }
  };

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page ds-page">
        <div className="wrap">
          <button type="button" className="op-back" onClick={() => navigate('/')}>
            <Msr>arrow_back</Msr>Dashboard
          </button>
          <div className="ph">
            <div>
              <h1>Default syllabi</h1>
              <div className="sub">
                <span>Each new feis starts with its region's default competitions. Changes here apply to feiseanna created from now on; existing events keep their own syllabus.</span>
              </div>
            </div>
          </div>

          <div className="ds-regions" role="tablist" aria-label="Region">
            {regions.map((r) => (
              <button
                key={r.name}
                type="button"
                role="tab"
                aria-selected={region === r.name}
                className={`op-chip${region === r.name ? ' on' : ''}`}
                onClick={() => setRegion(r.name)}
              >
                {r.name}
                <span className="ds-count">{r.count}</span>
              </button>
            ))}
          </div>

          <div className="op-stats">
            <div className="op-stat">
              <div className="op-stat-v">{rows.length}</div>
              <div className="op-stat-l">Competitions</div>
            </div>
            <div className="op-stat">
              <div className="op-stat-v">{groupNames.length}</div>
              <div className="op-stat-l">Levels</div>
            </div>
            <div className="op-stat">
              <div className="op-stat-v">{rows.filter((r) => r.championship).length}</div>
              <div className="op-stat-l">Championship</div>
              <div className="op-stat-s">count as championship for caps</div>
            </div>
          </div>

          <div className="toolbar ds-toolbar">
            <div className="op-search">
              <Msr>search</Msr>
              <input
                type="search"
                placeholder="Search code, dance or age"
                aria-label="Search competitions"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <select className="ds-select" aria-label="Level" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
              <option value="">All levels</option>
              {groupNames.map((g) => (
                <option key={g} value={g}>{titleCase(g)}</option>
              ))}
            </select>
            <button type="button" className="ab-next ds-add" onClick={openAdd}>
              <Msr>add</Msr>Add competition
            </button>
          </div>

          {notice && (
            <div className="ds-notice" role="status">
              <Msr>check_circle</Msr>
              {notice}
            </div>
          )}

          {error ? (
            <div className="op-error">
              {error}{' '}
              <button type="button" className="op-btn sm" onClick={() => loadRows(region)}>Try again</button>
            </div>
          ) : loading ? (
            <div className="loading">Loading {region}…</div>
          ) : rows.length === 0 ? (
            <div className="card op-panel op-empty">
              {region} has no default competitions yet. New feiseanna in this region start with an empty syllabus.
            </div>
          ) : visible.length === 0 ? (
            <div className="card op-panel op-empty">No competitions match.</div>
          ) : (
            visible.map((g) => (
              <section key={g.group} className="card op-panel" aria-label={titleCase(g.group)}>
                <div className="op-panel-h">
                  <div className="op-title" role="heading" aria-level="2">
                    {titleCase(g.group)} <span className="op-muted op-small">{g.rows.length}</span>
                  </div>
                </div>
                <div className="op-table" role="table">
                  <div className="op-tr op-th ds-tr" role="row">
                    <div role="columnheader">Code</div>
                    <div role="columnheader">Description</div>
                    <div role="columnheader">Ages</div>
                    <div role="columnheader">Dancers</div>
                    <div role="columnheader">Format</div>
                    <div role="columnheader" className="r">Price</div>
                    <div role="columnheader" aria-label="Actions" />
                  </div>
                  {g.rows.map((r) => (
                    <div key={r._id} className="op-tr ds-tr" role="row">
                      <div role="cell" data-label="Code"><span className="cv-code ds-code">{r.code}</span></div>
                      <div role="cell" data-label="Description" className="ds-desc">
                        {r.description}
                        {r.championship && <span className="op-pill open ds-pill">Championship</span>}
                        {r.second_chance && <span className="op-pill ds-pill">Second chance</span>}
                        <span className="ds-sub">
                          {agesLabel(r.ages)} · {dancersLabel(r)} · {formatRules(r)} · {money(r.price)}
                        </span>
                      </div>
                      <div role="cell" data-label="Ages" className="ds-meta">{agesLabel(r.ages)}</div>
                      <div role="cell" data-label="Dancers" className="ds-meta">{dancersLabel(r)}</div>
                      <div role="cell" data-label="Format" className="ds-meta">{formatRules(r)}</div>
                      <div role="cell" data-label="Price" className="r ds-meta">{money(r.price)}</div>
                      <div role="cell" className="ds-act">
                        <button type="button" className="kebab-btn" aria-label={`Edit ${r.code}`} onClick={() => openEdit(r)}>
                          <Msr>edit</Msr>
                        </button>
                        <button type="button" className="kebab-btn" aria-label={`Delete ${r.code}`} onClick={() => setPendingDelete(r)}>
                          <Msr>delete</Msr>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>

        {editor && (
          <RowEditor
            editor={editor}
            groups={editorGroups}
            region={region}
            onChange={setEditor}
            onCancel={() => setEditor(null)}
            onSave={save}
          />
        )}

        {pendingDelete && (
          <DeleteDialog
            row={pendingDelete}
            region={region}
            onKeep={() => setPendingDelete(null)}
            onRemove={confirmDelete}
          />
        )}
      </div>
    </>
  );
}

export default DefaultSyllabi;
